import io from 'socket.io-client';
import { ElLoading, ElMessage } from 'element-plus';

let loadingInstance = null;

export default class Socket {
  constructor(url, options = {}) {
    this.url = url;
    this.socket = null;
    this.isConnected = false;
    // 获取 Token 方法（支持外部动态更新）
    this.getToken = options.getToken;
    this.clientId = 'web-' + Math.random().toString(36).substr(2);

    this.isLoadingShowing = false;
    this.globalTimeoutId = null;
    this.subscriptions = new Map();
  }

  /**
   * 更新 Token 并重建 Socket 连接
   * 用于：用户重新登录、Token 刷新、权限变更
   */
  updateTokenAndReconnect() {
    if (!this.getToken) return;

    console.log('Token 已更新，正在重建 Socket 认证连接...');

    // 1. 清空所有超时和 loading，避免状态混乱
    this._clearAllResponseTimeout();

    // 2. 断开旧连接（必须，否则旧 auth 会一直生效）
    if (this.socket) {
      // 先移除所有旧事件监听，防止内存泄漏/重复触发
      this.socket.off();
      this.socket.disconnect();
      this.socket = null;
    }

    // 3. 重置连接状态
    this.isConnected = false;

    // 4. 使用新 Token 重新连接
    this.connect();
  }

  // 建立连接
  connect() {
    const token = this.getToken?.();
    if (!token) {
      console.warn('Socket 连接失败：未获取到 Token');
      return;
    }

    // 安全关闭旧连接
    if (this.socket) {
      this.socket.off();
      this.socket.disconnect();
    }

    console.log('开始连接 Socket.IO:', this.url);

    // 官方标准配置
    this.socket = io(this.url, {
      auth: {
        token,
      },
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: 10,
      // 重连时使用最新的 token 认证
      auth: (cb) => {
        cb({
          token: this.getToken?.() || '',
        });
      },
    });

    // 连接成功
    this.socket.on('connect', () => {
      console.log('Socket 已连接');
      this.isConnected = true;
      this._resubscribeAll();
    });

    // 连接错误
    this.socket.on('connect_error', (err) => {
      console.error('连接失败:', err.message);
      this.isConnected = false;
      ElMessage.error('Socket 连接失败：' + err.message);
    });

    // 断开连接
    this.socket.on('disconnect', (reason) => {
      console.log('断开连接:', reason);
      this.isConnected = false;

      // 主动断开的情况不再提示
      if (reason === 'io client disconnect') return;
      ElMessage.info('Socket 连接已断开');
    });
  }

  // 订阅
  subscribe(topic, callback) {
    if (!topic || !callback) return;
    this.subscriptions.set(topic, callback);
    if (this.socket) {
      this.socket.off(topic);
      this.socket.on(topic, (data) => {
        console.log(`收到 [${topic}] 消息:`, data);
        this._clearAllResponseTimeout();
        callback(data);
      });
    }
  }
  //取消订阅
  unsubscribe(topic) {
    if (this.subscriptions.has(topic)) {
      this.subscriptions.delete(topic);
      this.socket?.off(topic);
    }
  }

  unsubscribeAll() {
    this.subscriptions.forEach((_, topic) => {
      this.socket?.off(topic);
    });
    this.subscriptions.clear();
  }

  _resubscribeAll() {
    this.subscriptions.forEach((callback, topic) => {
      this.subscribe(topic, callback);
    });
  }

  // // 发送消息
  // send(data) {
  // 	if (!this.isConnected || !this.socket) {
  // 		ElMessage.warning('连接中，请稍后重试');
  // 		return;
  // 	}
  // 	this._startGlobalTimeout();
  // 	this.socket.emit('order.send', data);
  // 	console.log('发送消息',data)
  // }
  // 发送消息（改造后）
  send(data, skipSocketLoading = false) {
    if (!this.isConnected || !this.socket) {
      ElMessage.warning('连接中，请稍后重试');
      return;
    }
    // 把标记传入超时方法
    this._startGlobalTimeout(8000, skipSocketLoading, data);
    this.socket.emit('order.send', data);
    console.log('发送消息', data);
  }

  // Loading（改造后，新增 skipSocketLoading 参数）
  _startGlobalTimeout(
    timeout = 8000,
    skipSocketLoading = false,
    sendData = {},
  ) {
    this._clearAllResponseTimeout();

    // 消息是 attr.get 查询设备参数，自动跳过socket全局loading
    const isAttrGet = sendData?.payload?.action === 'attr.get';
    if (skipSocketLoading || isAttrGet) {
      // 不创建loading，只保留超时计时器（防止无响应日志）
      this.globalTimeoutId = setTimeout(() => {
        this._clearAllResponseTimeout();
        console.log('设备无响应，请重试');
      }, timeout);
      return;
    }

    if (!this.isLoadingShowing) {
      this.isLoadingShowing = true;
      loadingInstance = ElLoading.service({
        customClass: 'serLoading',
        background: 'rgba(0,0,0,0)',
      });
    }
    this.globalTimeoutId = setTimeout(() => {
      this._clearAllResponseTimeout();
      console.log('设备无响应，请重试');
    }, timeout);
  }

  // Loading
  // _startGlobalTimeout(timeout = 8000) {
  //   this._clearAllResponseTimeout();
  //   if (!this.isLoadingShowing) {
  //     this.isLoadingShowing = true;
  //     loadingInstance = ElLoading.service({
  //       text: '加载中',
  //       background: 'rgba(0,0,0,0.7)',
  //     });
  //   }
  //   this.globalTimeoutId = setTimeout(() => {
  //     this._clearAllResponseTimeout();
  //     // ElMessage.warning('设备无响应，请重试');
  //     console.log('设备无响应，请重试');
  //   }, timeout);
  // }

  clearResponseTimeout() {
    this._clearAllResponseTimeout();
  }

  _clearAllResponseTimeout() {
    clearTimeout(this.globalTimeoutId);
    this.globalTimeoutId = null;
    this._hideLoading();
  }

  _hideLoading() {
    if (this.isLoadingShowing && loadingInstance) {
      loadingInstance.close();
      loadingInstance = null;
      this.isLoadingShowing = false;
    }
  }

  // 关闭
  close() {
    this._clearAllResponseTimeout();
    this.unsubscribeAll();
    if (this.socket) {
      this.socket.off();
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    console.log(' Socket 已完全关闭并清理');
  }
  //销毁
  destroy() {
    this.close();
  }
}
